import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
  Rss,
  Plus,
  Trash2,
  RefreshCw,
  ExternalLink,
  Clock,
  CheckCircle,
  XCircle,
  Edit3,
  Globe,
  Zap,
  Loader2,
  AlertCircle,
  Send,
  X,
} from 'lucide-react';
import { useThemeStore } from '../../store/themeStore';
import { rssAPI, getErrorMessage } from '../../services/api';

/* ─── Types ─── */
interface RSSFeed {
  id: string;
  name: string;
  url: string;
  description: string | null;
  site_url: string | null;
  favicon: string | null;
  status: string;
  error_message: string | null;
  auto_publish: boolean;
  target_platforms: string[];
  publish_frequency: string;
  content_template: { prefix: string; suffix: string };
  hashtags: string[];
  max_posts_per_day: number;
  include_images: boolean;
  include_link: boolean;
  content_length_limit: number;
  last_fetched_at: string | null;
  last_published_at: string | null;
  total_items_fetched: number;
  total_posts_created: number;
  created_at: string;
}

interface RSSItem {
  guid: string;
  title: string;
  description: string;
  url: string;
  pub_date: string | null;
  image: string | null;
  author: string | null;
  categories: string[];
}

interface FormData {
  url: string;
  name: string;
  target_platforms: string[];
  auto_publish: boolean;
  publish_frequency: string;
  content_template_prefix: string;
  content_template_suffix: string;
  hashtags: string;
  max_posts_per_day: number;
  include_images: boolean;
  include_link: boolean;
  content_length_limit: number;
}

interface FeedStats {
  total_feeds: number;
  active_feeds: number;
  total_posts_created: number;
  total_items_fetched: number;
}

const PLATFORM_OPTIONS = ['twitter', 'linkedin', 'instagram', 'facebook', 'tiktok'];

const EMPTY_FORM: FormData = {
  url: '',
  name: '',
  target_platforms: [],
  auto_publish: true,
  publish_frequency: 'new',
  content_template_prefix: '',
  content_template_suffix: '',
  hashtags: '',
  max_posts_per_day: 3,
  include_images: true,
  include_link: true,
  content_length_limit: 280,
};

/* ─── Helper ─── */
function timeAgo(isoStr: string | null): string {
  if (!isoStr) return 'never';
  const diff = Date.now() - new Date(isoStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(isoStr).toLocaleDateString();
}

/* ─── Component ─── */
const RSSFeedsPage: React.FC = () => {
  const { t } = useTranslation();
  const { theme } = useThemeStore();
  const pc = theme.hex?.primary || '#10b981';
  const queryClient = useQueryClient();

  const [selectedFeedId, setSelectedFeedId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>({ ...EMPTY_FORM });

  /* ─── Queries ─── */
  const { data: feedsData, isLoading } = useQuery({
    queryKey: ['rss-feeds'],
    queryFn: async () => {
      const { data } = await rssAPI.list();
      return data as { feeds: RSSFeed[] };
    },
  });
  const feeds = feedsData?.feeds || [];

  const { data: statsData } = useQuery({
    queryKey: ['rss-stats'],
    queryFn: async () => {
      const { data } = await rssAPI.stats();
      return data as FeedStats;
    },
  });

  const { data: itemsData, isLoading: loadingItems } = useQuery({
    queryKey: ['rss-items', selectedFeedId],
    queryFn: async () => {
      if (!selectedFeedId) return { items: [] };
      const { data } = await rssAPI.getItems(selectedFeedId);
      return data as { items: RSSItem[] };
    },
    enabled: !!selectedFeedId,
  });
  const feedItems = itemsData?.items || [];

  const selectedFeed = feeds.find((f) => f.id === selectedFeedId);

  /* ─── Mutations ─── */
  const createMutation = useMutation({
    mutationFn: (data: FormData) =>
      rssAPI.create({
        name: data.name,
        url: data.url,
        target_platforms: data.target_platforms,
        auto_publish: data.auto_publish,
        publish_frequency: data.publish_frequency,
        content_template_prefix: data.content_template_prefix,
        content_template_suffix: data.content_template_suffix,
        hashtags: data.hashtags ? data.hashtags.split(',').map((h) => h.trim()).filter(Boolean) : [],
        max_posts_per_day: data.max_posts_per_day,
        include_images: data.include_images,
        include_link: data.include_link,
        content_length_limit: data.content_length_limit,
      }),
    onSuccess: (resp) => {
      toast.success(t('rss_feed_added', 'Feed added'));
      queryClient.invalidateQueries({ queryKey: ['rss-feeds'] });
      queryClient.invalidateQueries({ queryKey: ['rss-stats'] });
      setIsModalOpen(false);
      setSelectedFeedId(resp.data.feed.id);
    },
    onError: (err: unknown) => {
      toast.error(getErrorMessage(err, t('rss_add_feed_failed', 'Failed to add feed')));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: FormData }) =>
      rssAPI.update(id, {
        name: data.name,
        url: data.url,
        target_platforms: data.target_platforms,
        auto_publish: data.auto_publish,
        publish_frequency: data.publish_frequency,
        content_template_prefix: data.content_template_prefix,
        content_template_suffix: data.content_template_suffix,
        hashtags: data.hashtags ? data.hashtags.split(',').map((h) => h.trim()).filter(Boolean) : [],
        max_posts_per_day: data.max_posts_per_day,
        include_images: data.include_images,
        include_link: data.include_link,
        content_length_limit: data.content_length_limit,
      }),
    onSuccess: () => {
      toast.success(t('rss_feed_updated', 'Feed updated'));
      queryClient.invalidateQueries({ queryKey: ['rss-feeds'] });
      setIsModalOpen(false);
    },
    onError: (err: unknown) => toast.error(getErrorMessage(err, t('rss_update_failed', 'Update failed'))),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => rssAPI.delete(id),
    onSuccess: (_, id) => {
      toast.success(t('rss_feed_deleted', 'Feed deleted'));
      if (selectedFeedId === id) setSelectedFeedId(null);
      queryClient.invalidateQueries({ queryKey: ['rss-feeds'] });
      queryClient.invalidateQueries({ queryKey: ['rss-stats'] });
    },
    onError: () => toast.error(t('rss_delete_failed', 'Delete failed')),
  });

  const toggleMutation = useMutation({
    mutationFn: (id: string) => rssAPI.toggle(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rss-feeds'] });
      toast.success(t('rss_status_updated', 'Feed status updated'));
    },
  });

  const refreshMutation = useMutation({
    mutationFn: (id: string) => rssAPI.refresh(id),
    onSuccess: (resp) => {
      const newCount = resp.data.new_count || 0;
      toast.success(newCount > 0 ? t('rss_found_items', `Found ${newCount} new items`) : t('rss_up_to_date', 'Feed is up to date'));
      queryClient.invalidateQueries({ queryKey: ['rss-feeds'] });
      queryClient.invalidateQueries({ queryKey: ['rss-items', resp.data.feed.id] });
    },
    onError: (err: unknown) => toast.error(getErrorMessage(err, t('rss_refresh_failed', 'Refresh failed'))),
  });

  const postItemMutation = useMutation({
    mutationFn: ({ feedId, item }: { feedId: string; item: RSSItem }) =>
      rssAPI.postItem(feedId, {
        item_guid: item.guid,
        item_title: item.title,
        item_url: item.url,
        item_image: item.image || undefined,
      }),
    onSuccess: () => {
      toast.success(t('rss_draft_created', 'Draft post created!'));
      queryClient.invalidateQueries({ queryKey: ['rss-feeds'] });
    },
    onError: (err: unknown) => toast.error(getErrorMessage(err, t('rss_post_failed', 'Failed to create post'))),
  });

  /* ─── Modal handlers ─── */
  const openModal = (feedId?: string) => {
    if (feedId) {
      const feed = feeds.find((f) => f.id === feedId);
      if (feed) {
        setEditingId(feedId);
        setFormData({
          url: feed.url,
          name: feed.name,
          target_platforms: feed.target_platforms,
          auto_publish: feed.auto_publish,
          publish_frequency: feed.publish_frequency,
          content_template_prefix: feed.content_template.prefix,
          content_template_suffix: feed.content_template.suffix,
          hashtags: feed.hashtags.join(', '),
          max_posts_per_day: feed.max_posts_per_day,
          include_images: feed.include_images,
          include_link: feed.include_link,
          content_length_limit: feed.content_length_limit,
        });
      }
    } else {
      setEditingId(null);
      setFormData({ ...EMPTY_FORM });
    }
    setIsModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.url || !formData.name) {
      toast.error(t('rss_url_name_required', 'Feed URL and name are required'));
      return;
    }
    if (editingId) {
      updateMutation.mutate({ id: editingId, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  /* ─── Input style helper ─── */
  const inputCls = 'w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-gray-300 text-xs';
  const labelCls = 'block text-xs font-semibold text-gray-700 mb-1';

  /* ─── Render ─── */
  return (
    <div className="bg-gray-100 min-h-full">
      <div className="px-4 pt-3 pb-4">

      {/* Toolbar */}
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[10px] font-semibold px-2 py-1 rounded-full" style={{ backgroundColor: `${pc}15`, color: pc }}>
          {statsData?.active_feeds ?? feeds.filter((f) => f.status === 'active').length} {t('rss_active', 'active')}
        </span>
        <span className="text-[10px] font-semibold px-2 py-1 rounded-full bg-blue-50 text-blue-600">
          {statsData?.total_posts_created ?? 0} {t('rss_posts', 'posts')}
        </span>
        <span className="text-[10px] font-semibold px-2 py-1 rounded-full bg-purple-50 text-purple-600">
          {statsData?.total_items_fetched ?? 0} {t('rss_fetched', 'fetched')}
        </span>
        <div className="flex-1" />
        <button
          onClick={() => openModal()}
          className="flex items-center gap-1.5 px-3 py-1.5 text-white rounded-lg font-medium text-xs transition-colors hover:opacity-90"
          style={{ backgroundColor: pc }}
        >
          <Plus className="w-3.5 h-3.5" />
          {t('rss_add_feed', 'Add Feed')}
        </button>
      </div>

      {/* Main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* Feed list (left) */}
        <div>
          <div className="bg-white rounded-xl border border-gray-200">
          {isLoading ? (
            <div className="flex items-center justify-center py-8 text-gray-400 text-xs">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> {t('rss_loading_feeds', 'Loading feeds...')}
            </div>
          ) : feeds.length === 0 ? (
            <div className="text-center py-10">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: `${pc}15` }}>
                <Rss className="w-5 h-5" style={{ color: pc }} />
              </div>
              <p className="text-sm font-bold text-gray-900 mb-1">{t('rss_no_feeds', 'No feeds yet')}</p>
              <p className="text-xs text-gray-500">{t('rss_no_feeds_desc', 'Add an RSS feed to get started')}</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {feeds.map((feed) => (
                <div
                  key={feed.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedFeedId(feed.id)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedFeedId(feed.id); } }}
                  className={`p-3 cursor-pointer transition-colors hover:bg-gray-50 ${
                    selectedFeedId === feed.id ? 'bg-gray-50' : ''
                  }`}
                  style={{ borderLeft: `3px solid ${feed.status === 'active' ? '#10b981' : feed.status === 'error' ? '#ef4444' : '#d1d5db'}` }}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="min-w-0 flex-1 mr-2">
                      <h3 className="text-xs font-semibold text-gray-900 truncate">{feed.name}</h3>
                      <p className="text-[10px] text-gray-400 truncate">{feed.url}</p>
                    </div>
                    <div className="flex gap-1 shrink-0" role="presentation" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => refreshMutation.mutate(feed.id)}
                        disabled={refreshMutation.isPending}
                        className="p-1 text-gray-400 hover:text-gray-700 transition-colors"
                        title={t('rss_refresh', 'Refresh')}
                      >
                        <RefreshCw size={15} className={refreshMutation.isPending ? 'animate-spin' : ''} />
                      </button>
                      <button
                        onClick={() => openModal(feed.id)}
                        className="p-1 text-gray-400 hover:text-gray-700 transition-colors"
                        title={t('rss_edit', 'Edit')}
                      >
                        <Edit3 size={15} />
                      </button>
                      <button
                        onClick={() => deleteMutation.mutate(feed.id)}
                        className="p-1 text-red-400 hover:text-red-600 transition-colors"
                        title={t('rss_delete', 'Delete')}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>

                  <div className="flex gap-3 text-[10px] text-gray-500 mb-2">
                    <span className="flex items-center gap-0.5">
                      {feed.status === 'active' ? (
                        <CheckCircle size={11} className="text-green-500" />
                      ) : feed.status === 'error' ? (
                        <AlertCircle size={11} className="text-red-500" />
                      ) : (
                        <XCircle size={11} className="text-gray-400" />
                      )}
                      {feed.status}
                    </span>
                    <span className="flex items-center gap-0.5">
                      <Zap size={11} />
                      {feed.total_posts_created} {t('rss_posts', 'posts')}
                    </span>
                    <span className="flex items-center gap-0.5">
                      <Clock size={11} />
                      {timeAgo(feed.last_fetched_at)}
                    </span>
                  </div>

                  {feed.error_message && (
                    <p className="text-[10px] text-red-500 mb-1.5 truncate" title={feed.error_message}>
                      {feed.error_message}
                    </p>
                  )}

                  <div className="flex items-center gap-1.5 flex-wrap">
                    {feed.target_platforms.map((p) => (
                      <span
                        key={p}
                        className="bg-green-50 text-green-800 text-[10px] font-semibold px-1.5 py-0.5 rounded"
                      >
                        {p}
                      </span>
                    ))}
                    <div className="ml-auto flex items-center gap-1.5 text-[10px] text-gray-500" role="presentation" onClick={(e) => e.stopPropagation()}>
                      <input
                        id={`feed-status-${feed.id}`}
                        type="checkbox"
                        checked={feed.status === 'active'}
                        onChange={() => toggleMutation.mutate(feed.id)}
                        className="cursor-pointer w-3 h-3"
                      />
                      <label htmlFor={`feed-status-${feed.id}`} className="cursor-pointer">
                        {feed.status === 'active' ? t('rss_active', 'Active') : t('rss_paused', 'Paused')}
                      </label>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          </div>
        </div>

        {/* Feed items (right) */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl border border-gray-200">
          {selectedFeed ? (
            <div>
              <div className="px-3 py-2.5 border-b border-gray-100 flex items-center justify-between">
                <h3 className="text-xs font-semibold text-gray-900">
                  {t('rss_recent_items', 'Recent Items')} — {selectedFeed.name}
                </h3>
                <span className="text-[10px] text-gray-400">
                  {loadingItems ? t('rss_loading', 'Loading...') : `${feedItems.length} ${t('rss_items', 'items')}`}
                </span>
              </div>

              {loadingItems ? (
                <div className="flex items-center justify-center py-8 text-gray-400 text-xs">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" /> {t('rss_fetching_feed', 'Fetching feed...')}
                </div>
              ) : feedItems.length === 0 ? (
                <div className="text-center py-10">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: `${pc}15` }}>
                    <Rss className="w-5 h-5" style={{ color: pc }} />
                  </div>
                  <p className="text-sm font-bold text-gray-900 mb-1">{t('rss_no_items', 'No items available')}</p>
                  <p className="text-xs text-gray-500">{t('rss_try_refresh', 'Try refreshing the feed')}</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {feedItems.map((item) => (
                    <div
                      key={item.guid}
                      className="p-3 hover:bg-gray-50 transition-colors"
                    >
                      {item.image && (
                        <img
                          src={item.image}
                          alt={item.title}
                          className="w-full h-28 object-cover rounded-lg mb-2"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      )}
                      <h3 className="text-xs font-semibold text-gray-900 mb-1">{item.title}</h3>
                      {item.description && (
                        <p className="text-[11px] text-gray-500 mb-2 line-clamp-2">{item.description}</p>
                      )}
                      {item.categories.length > 0 && (
                        <div className="flex gap-1 flex-wrap mb-2">
                          {item.categories.slice(0, 5).map((cat) => (
                            <span
                              key={cat}
                              className="bg-gray-100 text-gray-500 text-[10px] px-1.5 py-0.5 rounded"
                            >
                              {cat}
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2 text-[10px] text-gray-400">
                          <span className="flex items-center gap-0.5">
                            <Clock size={11} />
                            {timeAgo(item.pub_date)}
                          </span>
                          {item.author && <span>by {item.author}</span>}
                        </div>
                        <div className="flex gap-1.5">
                          <a
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
                          >
                            <ExternalLink size={11} />
                            {t('rss_view', 'View')}
                          </a>
                          <button
                            onClick={() =>
                              postItemMutation.mutate({ feedId: selectedFeed.id, item })
                            }
                            disabled={postItemMutation.isPending}
                            className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
                            style={{ backgroundColor: pc }}
                          >
                            {postItemMutation.isPending ? (
                              <Loader2 size={11} className="animate-spin" />
                            ) : (
                              <Send size={11} />
                            )}
                            {t('rss_post', 'Post')}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-10">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: `${pc}15` }}>
                <Globe className="w-5 h-5" style={{ color: pc }} />
              </div>
              <p className="text-sm font-bold text-gray-900 mb-1">{t('rss_select_feed', 'Select a feed to preview')}</p>
              <p className="text-xs text-gray-500">{t('rss_click_feed', 'Click on a feed to see recent items')}</p>
            </div>
          )}
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          role="presentation"
          onClick={() => setIsModalOpen(false)}
        >
          <div
            className="bg-white rounded-xl max-w-lg w-[90%] max-h-[90vh] overflow-y-auto border border-gray-200"
            role="presentation"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h3 className="text-sm font-bold text-gray-900">
                {editingId ? t('rss_edit_feed', 'Edit Feed') : t('rss_add_new_feed', 'Add New Feed')}
              </h3>
              <button onClick={() => setIsModalOpen(false)} className="p-1 hover:bg-gray-100 rounded transition-colors">
                <X size={16} className="text-gray-400" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-4 flex flex-col gap-3">
              {/* URL */}
              <div>
                <label htmlFor="feed-url" className={labelCls}>{t('rss_feed_url', 'Feed URL')} *</label>
                <input
                  id="feed-url"
                  type="url"
                  placeholder="https://feeds.example.com/rss"
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  className={inputCls}
                  required
                />
              </div>

              {/* Name */}
              <div>
                <label htmlFor="feed-name" className={labelCls}>{t('rss_feed_name', 'Feed Name')} *</label>
                <input
                  id="feed-name"
                  type="text"
                  placeholder="e.g., TechCrunch News"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={inputCls}
                  required
                />
              </div>

              {/* Platforms */}
              <div>
                <span className={labelCls}>{t('rss_target_platforms', 'Target Platforms')}</span>
                <div className="flex flex-wrap gap-2">
                  {PLATFORM_OPTIONS.map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          target_platforms: prev.target_platforms.includes(p)
                            ? prev.target_platforms.filter((x) => x !== p)
                            : [...prev.target_platforms, p],
                        }))
                      }
                      className="px-2.5 py-1 rounded-lg text-xs font-semibold border transition-all capitalize"
                      style={{
                        borderColor: formData.target_platforms.includes(p) ? pc : '#d1d5db',
                        backgroundColor: formData.target_platforms.includes(p) ? `${pc}20` : 'transparent',
                        color: formData.target_platforms.includes(p) ? pc : '#6b7280',
                      }}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              {/* Frequency */}
              <div>
                <span className={labelCls}>Publish Frequency</span>
                <select
                  value={formData.publish_frequency}
                  onChange={(e) => setFormData({ ...formData, publish_frequency: e.target.value })}
                  className={inputCls}
                >
                  <option value="new">Every new item</option>
                  <option value="daily">Daily digest</option>
                  <option value="weekly">Weekly digest</option>
                </select>
              </div>

              {/* Max posts + content limit */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="max-posts" className={labelCls}>Max Posts/Day</label>
                  <input
                    id="max-posts"
                    type="number"
                    min={1}
                    max={20}
                    value={formData.max_posts_per_day}
                    onChange={(e) => setFormData({ ...formData, max_posts_per_day: parseInt(e.target.value) || 1 })}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor="content-limit" className={labelCls}>Content Limit (chars)</label>
                  <input
                    id="content-limit"
                    type="number"
                    min={100}
                    max={5000}
                    value={formData.content_length_limit}
                    onChange={(e) => setFormData({ ...formData, content_length_limit: parseInt(e.target.value) || 280 })}
                    className={inputCls}
                  />
                </div>
              </div>

              {/* Templates */}
              <div>
                <label htmlFor="prefix-text" className={labelCls}>Prefix Text</label>
                <input
                  id="prefix-text"
                  type="text"
                  placeholder="e.g., 🔥 Breaking: "
                  value={formData.content_template_prefix}
                  onChange={(e) => setFormData({ ...formData, content_template_prefix: e.target.value })}
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="suffix-text" className={labelCls}>Suffix Text</label>
                <input
                  id="suffix-text"
                  type="text"
                  placeholder="e.g., #Tech #News"
                  value={formData.content_template_suffix}
                  onChange={(e) => setFormData({ ...formData, content_template_suffix: e.target.value })}
                  className={inputCls}
                />
              </div>

              {/* Hashtags */}
              <div>
                <label htmlFor="hashtags" className={labelCls}>Hashtags (comma-separated)</label>
                <input
                  id="hashtags"
                  type="text"
                  placeholder="#Technology, #News"
                  value={formData.hashtags}
                  onChange={(e) => setFormData({ ...formData, hashtags: e.target.value })}
                  className={inputCls}
                />
              </div>

              {/* Toggles */}
              <div className="flex flex-col gap-2">
                {[
                  { key: 'include_images' as const, label: 'Include images in posts' },
                  { key: 'include_link' as const, label: 'Include link to original article' },
                  { key: 'auto_publish' as const, label: 'Enable auto-publish' },
                ].map((toggle) => (
                  <label key={toggle.key} className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData[toggle.key]}
                      onChange={(e) => setFormData({ ...formData, [toggle.key]: e.target.checked })}
                      className="cursor-pointer w-3 h-3"
                    />
                    {toggle.label}
                  </label>
                ))}
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-2 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1 py-1.5 rounded-lg bg-gray-100 text-gray-700 text-xs font-semibold hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-white text-xs font-semibold transition-opacity hover:opacity-90 disabled:opacity-60"
                  style={{ backgroundColor: pc }}
                >
                  {isSaving && <Loader2 size={12} className="animate-spin" />}
                  {editingId ? 'Update Feed' : 'Add Feed'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default RSSFeedsPage;
